import React, {useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DemoVid from './demoVid';

export default function Navbar() {
  const [isHowOpen, setIsHowOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Sync mobile state on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) setIsMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <nav style={styles.nav}>
      <a href="/" style={styles.logoContainer}>
        <h1 style={styles.logo}>Shepherd</h1>
        <span style={styles.betaBadge}>BETA</span>
      </a>

      {/* Hamburger Icon for Mobile */}
      {isMobile && (
        <button onClick={toggleMenu} style={styles.hamburger}>
          <div style={{ ...styles.line, transform: isMenuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }} />
          <div style={{ ...styles.line, opacity: isMenuOpen ? 0 : 1 }} />
          <div style={{ ...styles.line, transform: isMenuOpen ? 'rotate(-45deg) translate(7px, -7px)' : 'none' }} />
        </button>
      )}

      {/* Links List */}
      <ul style={{ 
        ...styles.links, 
        ...(isMobile ? (isMenuOpen ? styles.mobileLinksActive : styles.mobileLinksHidden) : {}) 
      }}>
        <li style={isMobile ? styles.mobileNavItem : {}}>
          <button 
            onClick={() => { setIsHowOpen(true); if(isMobile) setIsMenuOpen(false); }} 
            style={styles.linkButton}
          >
            How it works
          </button>
          {isHowOpen && <DemoVid close={() => setIsHowOpen(false)} />}
        </li>

        <li 
          style={isMobile ? styles.mobileNavItem : styles.navItem}
          onMouseEnter={() => !isMobile && setActiveDropdown('creators')}
          onMouseLeave={() => !isMobile && setActiveDropdown(null)}
        >
          <a href="#creator" onClick={() => isMobile && setIsMenuOpen(false)} style={styles.link}>Creators</a>
          {!isMobile && activeDropdown === 'creators' && (
            <div style={styles.dropdown}>
              <p style={styles.dropdownText}>Become a creator and build interactive guides and share your expertise with the world.</p>
            </div>
          )}
        </li>

        <li 
          style={isMobile ? styles.mobileNavItem : styles.navItem}
          onMouseEnter={() => !isMobile && setActiveDropdown('users')}
          onMouseLeave={() => !isMobile && setActiveDropdown(null)}
        >
          <a href="#daily-users" onClick={() => isMobile && setIsMenuOpen(false)} style={styles.link}>Daily users</a>
          {!isMobile && activeDropdown === 'users' && (
            <div style={styles.dropdown}>
              <p style={styles.dropdownText}>For users who want step-by-step guidance on any website without leaving your tab.</p>
            </div>
          )}
        </li>

        <li style={isMobile ? styles.mobileNavItem : {}}>
          <a href="https://chromewebstore.google.com/detail/shepherd-build-or-follow/okjbbpkkanbhfpmmafpmijcekpjjcncl" target="_blank" rel="noreferrer" style={styles.navCta}>
            Install for Chrome
          </a>
        </li>
      </ul>
    </nav>
  );
}

const styles = {
  nav: {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 clamp(1.5rem, 5vw, 4rem)',
    height: 64,
    background: 'rgba(250,251,255,0.88)',
    backdropFilter: 'blur(8px)',
    borderBottom: '1px solid var(--border)',
  },
  logoContainer: {
    display: 'flex', alignItems: 'center', textDecoration: 'none', gap: '12px',
  },
  betaBadge: {
    fontSize: '10px', fontWeight: '700', color: '#666666',
    background: '#f1f3f4', border: '1px solid #e0e0e0',
    padding: '3px 7px', borderRadius: '6px', letterSpacing: '0.05em',
    textTransform: 'uppercase', display: 'inline-block', lineHeight: 1,
  },
  logo: { display: 'flex', alignItems: 'center', textDecoration: 'none', fontFamily: 'magnolia-script', color: 'var(--brand)'},
  links: {
    display: 'flex', gap: '2rem', listStyle: 'none', alignItems: 'center',
  },
  link: {
    textDecoration: 'none', fontSize: '0.9rem',
    color: 'var(--text-mid)', fontWeight: 400,
  },
  navCta: {
    background: 'var(--brand)', color: 'white', padding: '8px 18px',
    borderRadius: 'var(--radius-sm)', fontWeight: 500, fontSize: '0.9rem',
    textDecoration: 'none', transition: 'background 0.2s',
  },
  linkButton: {
    background: 'transparent', border: 'none', color: 'var(--text-mid)',
    fontSize: '0.9rem', fontWeight: 400, cursor: 'pointer',
  },
  // --- FIXED DROPDOWN STYLES ---
  navItem: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    height: '64px', // Matches Nav height so the hover doesn't flicker
  },
  dropdown: {
    position: 'absolute',
    top: '55px', // Sit right below the link
    left: '50%',
    transform: 'translateX(-50%)',
    width: '200px',
    background: 'white',
    padding: '12px 16px',
    borderRadius: '8px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
    border: '1px solid #eee',
    zIndex: 101,
  },
  dropdownText: {
    margin: 0,
    fontSize: '0.8rem',
    lineHeight: '1.4',
    color: '#666',
    textAlign: 'center',
  },
  nav: {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, // Elevated zIndex for mobile
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 clamp(1.5rem, 5vw, 4rem)',
    height: 64,
    background: 'rgba(250,251,255,0.88)',
    backdropFilter: 'blur(8px)',
    borderBottom: '1px solid var(--border)',
  },
  logoContainer: { display: 'flex', alignItems: 'center', textDecoration: 'none', gap: '12px' },
  betaBadge: { fontSize: '10px', fontWeight: '700', color: '#666666', background: '#f1f3f4', border: '1px solid #e0e0e0', padding: '3px 7px', borderRadius: '6px', textTransform: 'uppercase' },
  logo: { textDecoration: 'none', fontFamily: 'magnolia-script', color: 'var(--brand)', margin: 0 },
  
  links: {
    display: 'flex', gap: '2rem', listStyle: 'none', alignItems: 'center', margin: 0, padding: 0,
    transition: 'all 0.3s ease-in-out',
  },
  
  // Mobile specific overrides
  mobileLinksHidden: {
    position: 'absolute', top: 64, left: 0, right: 0, background: 'white',
    flexDirection: 'column', padding: '0', height: 0, overflow: 'hidden', opacity: 0,
  },
  mobileLinksActive: {
    position: 'absolute', top: 64, left: 0, right: 0, background: 'white',
    flexDirection: 'column', padding: '2rem 0', height: 'auto', borderBottom: '1px solid #eee',
    opacity: 1, boxShadow: '0 10px 20px rgba(0,0,0,0.05)',
  },
  mobileNavItem: {
    padding: '1rem 0',
    width: '100%',
    textAlign: 'center',
  },

  hamburger: {
    display: 'flex', flexDirection: 'column', gap: '5px',
    background: 'none', border: 'none', cursor: 'pointer', padding: '10px',
  },
  line: {
    width: '24px', height: '2px', background: 'var(--brand)',
    transition: '0.3s',
  },

  link: { textDecoration: 'none', fontSize: '1rem', color: 'var(--text-mid)', fontWeight: 400 },
  navCta: { background: 'var(--brand)', color: 'white', padding: '10px 24px', borderRadius: '8px', fontWeight: 500, textDecoration: 'none' },
  linkButton: { background: 'transparent', border: 'none', color: 'var(--text-mid)', fontSize: '1rem', cursor: 'pointer' },
  navItem: { position: 'relative', display: 'flex', alignItems: 'center', height: '64px' },
  dropdown: { position: 'absolute', top: '55px', left: '50%', transform: 'translateX(-50%)', width: '200px', background: 'white', padding: '12px 16px', borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', border: '1px solid #eee', zIndex: 101 },
  dropdownText: { margin: 0, fontSize: '0.8rem', lineHeight: '1.4', color: '#666', textAlign: 'center' }

};
